import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Star, Award, Building2, MapPin, Briefcase, ArrowRight } from "lucide-react";
import { prefixUrl } from "@/lib/api";

export default function AgentCard({ agent }) {
  const [imgError, setImgError] = useState(false);
  const id = agent.user_id || agent.id;
  const rating = Number(agent.rating || 4.8).toFixed(1);
  const reviewsCount = agent.reviews_count || 15;
  const experienceYears = agent.experience_years || 3;
  const propertyCount = agent.property_count || 0;
  
  const localities = agent.localities || agent.city;
  const specialisations = agent.specialisations;
  const officeName = agent.office_name;

  // Initials fallback calculation
  const getInitials = (name) => {
    if (!name) return "SH";
    const parts = name.split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <Link 
      to={`/agents/${id}`}
      className="premium-card premium-card-interactive bg-white border border-slate-200/80 rounded-2xl p-6 hover:-translate-y-1 transition duration-300 group flex flex-col justify-between h-full relative"
      data-testid={`agent-card-${id}`}
    >
      <div>
        {/* Top row: Avatar & Badges */}
        <div className="flex items-start justify-between mb-4">
          <div className="w-16 h-16 rounded-full bg-amber-50 text-amber-700 font-display font-black text-lg flex items-center justify-center overflow-hidden border border-amber-500/15 shrink-0">
            {agent.picture && !imgError ? (
              <img src={prefixUrl(agent.picture)} alt={agent.name} loading="lazy" decoding="async" onError={() => setImgError(true)} className="w-full h-full object-cover" />
            ) : (
              getInitials(agent.name)
            )}
          </div>

          <div className="flex flex-col items-end gap-1.5">
            {agent.verified_owner && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider border border-emerald-100">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> Verified Agent
              </span>
            )}
            {experienceYears > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-50 text-slate-600 text-[9.5px] font-bold border border-slate-100">
                {experienceYears}y Experience
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mb-6">
          <h3 className="font-display font-black text-lg text-slate-900 group-hover:text-amber-700 transition-colors truncate">
            {agent.name}
          </h3>
          {officeName && <p className="text-slate-500 text-xs font-semibold flex items-center gap-1 mt-1">
            <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" /> {officeName}
          </p>}

          <hr className="my-3.5 border-slate-100" />

          {/* Location & Specialty */}
          <div className="space-y-2">
            {localities && <p className="text-xs text-slate-600 flex items-start gap-1.5 leading-relaxed">
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              <span>
                <strong className="text-slate-800">Serves:</strong> {localities}
              </span>
            </p>}
            {specialisations && <p className="text-xs text-slate-600 flex items-start gap-1.5 leading-relaxed">
              <Briefcase className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              <span>
                <strong className="text-slate-800">Focus:</strong> {specialisations}
              </span>
            </p>}
          </div>
        </div>
      </div>

      {/* Stats Summary & CTA */}
      <div>
        <div className="grid grid-cols-2 gap-3 mb-5 bg-[#F8FAFC] p-3 rounded-xl border border-slate-100">
          <div className="text-center border-r border-slate-200/50 pr-2">
            <p className="font-display font-black text-base text-slate-900 leading-none">
              {propertyCount.toLocaleString("en-IN")}
            </p>
            <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider mt-1.5 leading-none">
              Listings
            </p>
          </div>
          <div className="text-center pl-2">
            {rating && reviewsCount > 0 ? (
              <>
                <p className="font-display font-black text-base text-slate-900 leading-none flex items-center justify-center gap-1">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" /> {rating}
                </p>
                <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider mt-1.5 leading-none">
                  ({reviewsCount} reviews)
                </p>
              </>
            ) : (
              <>
                <p className="font-display font-bold text-xs text-emerald-600 leading-none flex items-center justify-center gap-1 pt-1">
                  New
                </p>
                <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider mt-1.5 leading-none">
                  No reviews
                </p>
              </>
            )}
          </div>
        </div>

        {/* View Profile Action Link */}
        <div className="flex items-center justify-between text-xs font-bold text-amber-700 group-hover:text-amber-800 transition-colors pt-2 h-11">
          <span>View Profile</span>
          <span className="w-8 h-8 rounded-full bg-amber-50 text-amber-700 grid place-items-center group-hover:bg-amber-600 group-hover:text-white transition-all shrink-0">
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
